import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { storageService } from './StorageService';

export interface Announcement {
  id: string;
  title: string;
  message: string;
  createdAt: Date;
  expiresAt?: Date;
  priority: 'low' | 'normal' | 'high';
  active: boolean;
}

class AnnouncementService {
  private readonly COLLECTION_NAME = 'announcements';

  /**
   * Fetch active announcements from Firestore
   * Returns announcements that are:
   * - Active (active: true)
   * - Not expired (expiresAt is null or in the future)
   * - Not already dismissed by the user
   */
  async getActiveAnnouncements(): Promise<Announcement[]> {
    try {
      const now = Timestamp.now();
      const announcementsRef = collection(db, this.COLLECTION_NAME);
      
      // Query for active announcements
      const q = query(
        announcementsRef,
        where('active', '==', true)
      );

      const querySnapshot = await getDocs(q);
      const announcements: Announcement[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Check if announcement has expired
        if (data.expiresAt) {
          const expiresAt = data.expiresAt.toDate();
          if (expiresAt < new Date()) {
            return; // Skip expired announcements
          }
        }

        // Check if user has already dismissed this announcement
        if (storageService.isAnnouncementDismissed(doc.id)) {
          return; // Skip dismissed announcements
        }

        announcements.push({
          id: doc.id,
          title: data.title || '',
          message: data.message || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          expiresAt: data.expiresAt?.toDate(),
          priority: data.priority || 'normal',
          active: data.active,
        });
      });

      // Sort by priority (high > normal > low) and then by creation date (newest first)
      announcements.sort((a, b) => {
        const priorityOrder = { high: 3, normal: 2, low: 1 };
        const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return b.createdAt.getTime() - a.createdAt.getTime();
      });

      return announcements;
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
      return [];
    }
  }

  /**
   * Get the next announcement to show to the user
   * Returns null if no announcements are available
   */
  async getNextAnnouncement(): Promise<Announcement | null> {
    const announcements = await this.getActiveAnnouncements();
    return announcements.length > 0 ? announcements[0] : null;
  }

  /**
   * Mark an announcement as dismissed
   */
  dismissAnnouncement(announcementId: string): void {
    storageService.dismissAnnouncement(announcementId);
  }
}

// Export singleton instance
export const announcementService = new AnnouncementService();
